/* assets/of-search-settle-core.js (added 2026-08-06, search-analytics
 * pollution fix; corrected 2026-08-06 against industry research, see
 * search-fragment-research.md Part 3)
 *
 * Pure pending/supersede/commit state machine behind the fix for typing
 * fragments polluting search analytics. No DOM, no network, no side effects
 * at load. Dual-exported (window.OFSearchSettleCore in the browser,
 * module.exports in node) exactly like of-brands-core.js and of-help-index-
 * core.js, so every decision here is covered by tests/search-settle-core.
 * test.mjs.
 *
 * THE PROBLEM (measured against 30 days of live log data): three search
 * surfaces log a search event every time their render debounce settles.
 * Rendering has to be fast, so the debounce is short (150ms to 400ms
 * depending on the surface), and a short debounce still lets through
 * several rows per real search whenever someone pauses mid-phrase to think.
 * A 5-keystroke example from production, one session, inside 2 seconds:
 * "do you se" -> "do you sell re" -> "do you sell recycled" -> "do you sell
 * recycled silv" -> "do you sell recycled silver", each one logged. Only
 * the last is a real search.
 *
 * THE FIX splits rendering from logging into two stages. RENDER keeps
 * exactly the debounce it has today: this module changes nothing about how
 * fast results paint. LOG COMMIT is a second, slower stage built around a
 * single HIGH-WATER MARK: the longest, most-committed-looking query reached
 * in the current unbroken burst. A settled query updates or replaces the
 * mark; the mark itself is what eventually gets logged, via:
 *   - roughly COMMIT_IDLE_MS passes with no further input,
 *   - the caller explicitly flushes (submit, a result/suggestion click, the
 *     search panel closing, the input blurring, or the page unloading),
 *   - or a genuinely different query arrives, which commits the mark
 *     immediately and starts a fresh one.
 *
 * This design and its constants were checked against how the industry
 * actually does this (docs/superpowers/plans/... research, 2026-08-06).
 * Algolia is the one vendor with a named, documented mechanism for the same
 * problem ("query aggregation"): keystrokes are "queries", the analytics
 * unit is a "search", and aggregation keys on edit distance plus a time
 * window plus a stable user id. This module is architecturally the same
 * shape. Four corrections came out of that research and are load-bearing
 * here, not incidental:
 *
 * 1. THE HIGH-WATER MARK, not the literal last-typed string, is what
 *    commits. A strict "keep the pending value" rule silently destroys a
 *    failed search on the backspace branch: type "recycled silver", get
 *    nothing, backspace, type "recycled gold" instead, and a naive rule
 *    logs only "recycled gold" -- "recycled silver" vanishes, and it
 *    vanishes specifically BECAUSE it failed, which is a systematic bias
 *    against exactly the abandoned queries zero-results analysis exists to
 *    find. Algolia names this scenario directly: "It's important not to
 *    throw away the first full query because it provides valuable
 *    information, but it's also hard to determine whether it's a final
 *    search or a mistype." So: a forward extension of the mark ADVANCES the
 *    mark (unchanged from the original design). A contraction (backspacing
 *    below the mark) moves the CURRENT typed position backward but leaves
 *    the mark itself untouched -- it is a held branch, not a discard. If
 *    the burst then diverges onto a path that is not a continuation of the
 *    mark, the mark commits as its own completed, abandoned search before
 *    the new candidate starts. If the burst instead ends (idle or an
 *    explicit flush) while still contracted with no divergence, the mark
 *    still commits, not the shorter, currently-visible remnant: someone who
 *    types "recycled silver", backspaces to "recycled sil", and walks away
 *    searched for "recycled silver".
 *
 * 2. THE STRING TEST IS PREFIX **OR** BOUNDED EDIT DISTANCE, not prefix
 *    alone. Jones and Klinkner (CIKM 2008) and Huang and Efthimiadis (CIKM
 *    2009) both find edit distance is the strong predictor of "same
 *    underlying search" (85 percent accuracy alone, beating every
 *    time-only threshold they tested), and Algolia lists edit distance
 *    first among its three aggregation criteria. A strict prefix test
 *    misses the common case of a typo correction mid-burst: "silvre" then
 *    "silver" is not a prefix relation in either direction, so a
 *    prefix-only rule would commit "silvre" as a real search and
 *    manufacture a phantom zero-result. The escape hatch here is narrow on
 *    purpose (distance <= MAX_EDIT_DISTANCE, and only within
 *    EDIT_CONTINUATION_GAP_MS of the previous settle): past a distance of 2,
 *    unrelated words start looking related ("silver" and "solder" are
 *    already distance 2), so the bound does not grow further.
 *
 * 3. COMMIT_IDLE_MS is 4000, not 1200. Huang and Efthimiadis measured
 *    median inter-query gaps for genuine query reformulation on real search
 *    logs: the fastest type, spelling correction, sits at 22 seconds;
 *    prefix extension itself sits at 53 seconds. Algolia's own window is 30
 *    seconds. There is a near-empty band between roughly 1.2 seconds and
 *    roughly 20 seconds where almost nothing but typing lives. At 1200ms,
 *    any shopper who pauses over a second to glance at results, hunt for a
 *    key, think about the next word, or type on a phone gets split, which
 *    is exactly the fragmentation this module exists to remove. 4000ms
 *    clears realistic human typing pauses with room to spare while staying
 *    five to thirteen times below the fastest genuine reformulation median,
 *    so it does not meaningfully risk merging two real, distinct searches.
 *
 * 5. COMMIT_IDLE_MS IS 15000, RAISED FROM 4000 ON 2026-09-02. Correction 3
 *    below picked 4000 by reasoning about typing speed. Live evidence says
 *    typing speed is not the variable that matters. Rhett's Insights Help
 *    tab on 2026-09-01 showed one shopper's single question filed as four
 *    questions: "i'm looking for", "i'm looking for soft", "i'm looking for
 *    soft todd", "i'm looking for half soft coil sterling silver". Every
 *    boundary there is a THINKING pause inside one unfinished sentence, and
 *    conversational help questions are full of them: people compose out
 *    loud, stop to find the word, and carry on. Four seconds does not clear
 *    that, and the proof that a pause was a typing pause rather than an
 *    abandonment is that the customer came back and CONTINUED THE SAME
 *    STRING.
 *
 *    Raising the window changes behavior in exactly one band and one
 *    direction. For a gap between 4 and 15 seconds: an extension, a
 *    contraction, an exact repeat or a last-word correction now stays in
 *    the burst instead of splitting it, and anything else still commits the
 *    mark on the spot, because divergence has never waited on this timer.
 *    Nothing new is merged that is not literally a continuation of the same
 *    string. 15000 also stays below the 22 second median that correction 3
 *    is built on, so the reformulation band is still out of reach.
 *
 * 6. A LAST-WORD CORRECTION MID-SENTENCE IS A CONTINUATION, not a
 *    divergence. The same screenshot carried "...what hardness silva" and
 *    "...what hardness silver do i need" as two questions. They share
 *    thirteen identical leading words; the customer mistyped one word,
 *    fixed it, and kept going. That is not a prefix relation in either
 *    direction, and the correction-2 escape hatch cannot see it either,
 *    because the whole-string distance between them is the length of the
 *    words that came after. isTypingContinuation tests the shape that
 *    actually happened: an identical stem, then one final typed word that
 *    the longer query CORRECTED and typed past. Its bounds are deliberately
 *    narrower than correction 2's: a stem of at least one whole word, a
 *    strictly longer query, and a diverging word that shares three leading
 *    characters AND sits within MAX_EDIT_DISTANCE, so "silva" to "silver"
 *    passes and "silver" to "solder" cannot.
 *
 * 4. BLANK AND EXACT-REPEAT ARE GUARDED EXPLICITLY. A cleared box is not a
 *    contraction of the mark down to nothing, and must never itself commit
 *    or discard anything (that policy choice belongs to the caller, which
 *    already calls reset() when its own floor is crossed). An exact repeat
 *    of the mark (which does happen: a deferred re-render of the identical
 *    settled query, or a customer who reads the field and retypes what was
 *    already there) refreshes the mark's meta -- so a later, more complete
 *    render of the SAME query is what gets logged -- without advancing
 *    anything or ever producing a second row.
 *
 * This module owns none of the actual event names or params -- every
 * consumer (of-help-hub.js, of-brands.js, of-instant-search.js) supplies
 * its own onCommit callback and decides what to log with the meta it
 * already had at settle() time. The existing once-per-distinct-query
 * guards on each surface (searchSeen, seen, panelViewSeenQueries, etc.)
 * stay exactly where they are; they just key off the COMMITTED query,
 * inside onCommit, so a superseded fragment never occupies the guard.
 * Nothing about the public API changed in the correction pass above (still
 * settle/flush/reset/pendingQuery), so no consumer file needed edits.
 *
 * TIMERS AND CLOCK: createSettleTracker schedules the idle commit with a
 * real setTimeout/clearTimeout by default, and reads elapsed time with
 * Date.now by default (all exist in every environment this ships to,
 * browser and node). Tests inject their own scheduler and clock so the
 * idle-timeout and gap-dependent behavior is provable without a real wait
 * -- see tests/search-settle-core.test.mjs.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.OFSearchSettleCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  /* 15000ms, raised from 4000 on 2026-09-02. See corrections 3 and 5 in the
   * file header. 4000 was reasoned from typing speed; the live Help tab
   * proved the binding variable is THINKING pauses inside one unfinished
   * conversational question, which run well past four seconds. This is how
   * long the tracker holds an uncommitted mark waiting to see whether the
   * customer is still writing the same sentence. It bounds continuations
   * only: a genuinely different query commits the mark the moment it
   * arrives and has never waited on this timer. 15000 still sits below the
   * 22 second median inter-query gap for the fastest genuine reformulation
   * (spelling correction, Huang and Efthimiadis CIKM 2009), so the
   * reformulation band stays out of reach; Algolia's own window is 30
   * seconds and reaches into it, which is why 30 is still not copied.
   */
  var COMMIT_IDLE_MS = 15000;

  /* The bounded edit-distance escape hatch (correction 2): how many single-
   * character edits away from the high-water mark still counts as "the same
   * search, just corrected", and how recently the previous settle has to
   * have happened for that to apply. Both stay small and both are load
   * bearing together: distance alone would eventually conflate unrelated
   * short words, and a long gap alone would let old typos hang around and
   * absorb something new. Huang and Efthimiadis use exactly this distance
   * bound for their own spelling-correction reformulation rule.
   */
  var MAX_EDIT_DISTANCE = 2;
  var EDIT_CONTINUATION_GAP_MS = 2000;

  function defaultNormalize(q) {
    return String(q == null ? '' : q).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  // Exported standalone so both the tracker and the tests can reason about
  // the supersede rule without constructing a tracker. True when either
  // string starts with the other (equal strings count as related too).
  function isPrefixRelated(a, b) {
    var x = String(a == null ? '' : a);
    var y = String(b == null ? '' : b);
    if (x === y) return true;
    if (!x || !y) return false;
    return x.indexOf(y) === 0 || y.indexOf(x) === 0;
  }

  // Exact Levenshtein distance, no early exit -- for tests and for callers
  // that want the real number rather than a bounded yes/no. Standard
  // iterative two-row DP.
  function levenshtein(a, b) {
    var x = String(a == null ? '' : a);
    var y = String(b == null ? '' : b);
    var lx = x.length;
    var ly = y.length;
    if (!lx) return ly;
    if (!ly) return lx;
    var prev = new Array(ly + 1);
    var curr = new Array(ly + 1);
    for (var j = 0; j <= ly; j++) prev[j] = j;
    for (var i = 1; i <= lx; i++) {
      curr[0] = i;
      var cx = x.charCodeAt(i - 1);
      for (var j2 = 1; j2 <= ly; j2++) {
        var cost = cx === y.charCodeAt(j2 - 1) ? 0 : 1;
        curr[j2] = Math.min(prev[j2] + 1, curr[j2 - 1] + 1, prev[j2 - 1] + cost);
      }
      var tmp = prev;
      prev = curr;
      curr = tmp;
    }
    return prev[ly];
  }

  /* Bounded distance check with two cheap early exits, so the hot settle()
   * path never pays for a full DP on strings that cannot possibly be close:
   *   1. a length difference alone bigger than maxDist rules it out before
   *      any DP runs at all.
   *   2. once every cell in a DP row already exceeds maxDist, no later row
   *      can recover (each further row can only add cost), so the scan
   *      stops there instead of finishing the matrix.
   * Query strings here are short (a handful of words), so this is a minor
   * optimization more than a necessity, but it is the shape the mission
   * asked for and it costs nothing to keep.
   */
  function withinEditDistance(a, b, maxDist) {
    var x = String(a == null ? '' : a);
    var y = String(b == null ? '' : b);
    if (x === y) return true;
    var lx = x.length;
    var ly = y.length;
    if (Math.abs(lx - ly) > maxDist) return false;
    var prev = new Array(ly + 1);
    var curr = new Array(ly + 1);
    for (var j = 0; j <= ly; j++) prev[j] = j;
    for (var i = 1; i <= lx; i++) {
      curr[0] = i;
      var rowMin = curr[0];
      var cx = x.charCodeAt(i - 1);
      for (var j2 = 1; j2 <= ly; j2++) {
        var cost = cx === y.charCodeAt(j2 - 1) ? 0 : 1;
        curr[j2] = Math.min(prev[j2] + 1, curr[j2 - 1] + 1, prev[j2 - 1] + cost);
        if (curr[j2] < rowMin) rowMin = curr[j2];
      }
      if (rowMin > maxDist) return false;
      var tmp = prev;
      prev = curr;
      curr = tmp;
    }
    return prev[ly] <= maxDist;
  }

  /* Correction 6: the mid-sentence last-word correction.
   *
   * TRUE when `query` is `markQuery` with its final typed word fixed and the
   * sentence carried on past it. Every earlier word matches exactly, the
   * query is strictly longer in words, and the one word that changed is a
   * near miss of what it became rather than a different word.
   *
   * The three bounds are all load bearing and all narrower than correction
   * 2's whole-string escape hatch, because this one is not time bounded:
   *   - a stem of at least one whole identical leading word, so a
   *     single-word query can never continue into another single-word query
   *     ("silver" into "solder wire" is refused here and left to the
   *     correction-2 branch, which bounds it to 2 seconds).
   *   - strictly more words, which is what makes it a continuation rather
   *     than a reformulation: the customer typed past the fix.
   *   - the changed word shares three leading characters AND sits within
   *     maxDist. "silva" into "silver" shares "silv" at distance 2 and
   *     passes; "silver" into "solder" shares "s" and cannot, even though
   *     its distance is also 2. Distance alone is exactly the conflation
   *     correction 2 warns about, so it does not stand alone here.
   */
  var CONTINUATION_STEM_CHARS = 3;

  function tokenizeQuery(q) {
    return String(q == null ? '' : q).split(' ').filter(function (t) { return !!t; });
  }

  function commonPrefixChars(a, b) {
    var x = String(a == null ? '' : a);
    var y = String(b == null ? '' : b);
    var n = Math.min(x.length, y.length);
    var i = 0;
    while (i < n && x.charCodeAt(i) === y.charCodeAt(i)) i++;
    return i;
  }

  function isTypingContinuation(markQuery, query, maxDist) {
    var bound = typeof maxDist === 'number' ? maxDist : MAX_EDIT_DISTANCE;
    var m = tokenizeQuery(markQuery);
    var n = tokenizeQuery(query);
    if (m.length < 2) return false;
    if (n.length <= m.length) return false;
    for (var i = 0; i < m.length - 1; i++) {
      if (m[i] !== n[i]) return false;
    }
    var typed = m[m.length - 1];
    var became = n[m.length - 1];
    if (typed === became) return false;
    if (commonPrefixChars(typed, became) < CONTINUATION_STEM_CHARS) return false;
    return withinEditDistance(typed, became, bound);
  }

  /* Stateful factory, same shape as createHelpIndexLoader in of-help-index-
   * core.js: closures hold the mutable state (the high-water mark, the
   * idle timer handle, the last-settle timestamp), the returned object is
   * the whole public API. The public API is UNCHANGED from before the
   * correction pass (settle/flush/reset/pendingQuery), so no consumer file
   * needed to change.
   *
   * opts:
   *   idleMs        commit delay, default COMMIT_IDLE_MS
   *   onCommit(query, meta)  called once per commit, with whatever meta was
   *                 attached to the mark at the moment it last advanced (or
   *                 was refreshed by an exact repeat)
   *   normalize(q)  defaults to trim/lowercase/collapse-whitespace; pass
   *                 your own if the surface already normalizes differently
   *                 and settle() should not double-normalize
   *   maxEditDistance / editGapMs  override the escape-hatch bounds,
   *                 default MAX_EDIT_DISTANCE / EDIT_CONTINUATION_GAP_MS
   *   setTimeout / clearTimeout  injectable scheduler, defaults to the
   *                 real global functions. Tests supply a fake so the
   *                 idle-timeout path never waits in real time.
   *   now()         injectable clock, defaults to Date.now. Tests supply a
   *                 fake so the edit-distance gap check is provable without
   *                 a real wait.
   */
  function createSettleTracker(opts) {
    opts = opts || {};
    var idleMs = typeof opts.idleMs === 'number' ? opts.idleMs : COMMIT_IDLE_MS;
    var maxEditDistance = typeof opts.maxEditDistance === 'number' ? opts.maxEditDistance : MAX_EDIT_DISTANCE;
    var editGapMs = typeof opts.editGapMs === 'number' ? opts.editGapMs : EDIT_CONTINUATION_GAP_MS;
    var onCommit = typeof opts.onCommit === 'function' ? opts.onCommit : function () {};
    var normalize = typeof opts.normalize === 'function' ? opts.normalize : defaultNormalize;
    var setTimer = typeof opts.setTimeout === 'function'
      ? opts.setTimeout
      : (typeof setTimeout !== 'undefined' ? setTimeout : null);
    var clearTimer = typeof opts.clearTimeout === 'function'
      ? opts.clearTimeout
      : (typeof clearTimeout !== 'undefined' ? clearTimeout : null);
    var clock = typeof opts.now === 'function'
      ? opts.now
      : (typeof Date !== 'undefined' ? Date.now.bind(Date) : function () { return 0; });

    // The high-water mark: the candidate that will actually be logged.
    // { query, meta } or null. See correction 1 in the file header for why
    // this, and not "whatever the customer's field currently shows", is
    // the thing that commits.
    var mark = null;
    var lastSettleAt = null;
    var timerHandle = null;

    function clearIdleTimer() {
      if (timerHandle !== null && clearTimer) clearTimer(timerHandle);
      timerHandle = null;
    }

    function startIdleTimer() {
      clearIdleTimer();
      if (!setTimer) return;
      timerHandle = setTimer(function () { flush(); }, idleMs);
    }

    // Commits the mark (if there is one) and clears all state. Idempotent:
    // calling flush() with no mark is a no-op, never a duplicate onCommit
    // -- the once-per-distinct-query dedupe still belongs to the caller's
    // own guard, keyed on the committed query.
    function flush() {
      clearIdleTimer();
      if (!mark) return;
      var candidate = mark;
      mark = null;
      lastSettleAt = null;
      onCommit(candidate.query, candidate.meta);
    }

    // A render debounce settled on `rawQuery`. Decides what happens to the
    // high-water mark; never calls onCommit synchronously for the query
    // just passed in (that only happens later, via idle timeout or an
    // explicit flush) -- EXCEPT when a genuinely different query arrives,
    // which commits the OLD mark (never the new one) immediately.
    function settle(rawQuery, meta) {
      var query = normalize(rawQuery);
      // Correction 4: blank is never a candidate, never a contraction of
      // the mark down to nothing, and never itself commits or discards
      // anything. A caller that wants a cleared box to discard the mark
      // calls reset() explicitly (its own policy, not this module's).
      if (!query) return;

      var now = clock();
      var gap = lastSettleAt === null ? Infinity : (now - lastSettleAt);
      lastSettleAt = now;

      if (!mark) {
        mark = { query: query, meta: meta };
        startIdleTimer();
        return;
      }

      // Correction 4: an exact repeat of the mark refreshes its meta (a
      // later, more complete render of the identical query should be what
      // gets logged) without advancing anything and without ever counting
      // as a second candidate.
      if (query === mark.query) {
        mark.meta = meta;
        startIdleTimer();
        return;
      }

      if (isPrefixRelated(mark.query, query)) {
        if (query.length > mark.query.length) {
          // Forward extension past the mark: advance it. Unchanged from
          // the original design, and the branch where the 35-75 percent
          // fragment volume actually lives.
          mark = { query: query, meta: meta };
        }
        // Else: a contraction below the mark, or a re-extension that has
        // not yet reached the mark again. Correction 1: the mark itself is
        // a HELD BRANCH now, not overwritten -- it still holds whatever it
        // last advanced to, and it is still what commits at idle/flush.
        startIdleTimer();
        return;
      }

      // Correction 6: same sentence, last typed word corrected and typed
      // past. Not a prefix relation and too far apart for correction 2's
      // whole-string test, but structurally a continuation. Advances the
      // mark exactly like an extension.
      if (isTypingContinuation(mark.query, query, maxEditDistance)) {
        mark = { query: query, meta: meta };
        startIdleTimer();
        return;
      }

      // Not a prefix relation with the mark. Correction 2: a narrow, fast
      // edit is still the same search, not a new one -- collapses into the
      // mark exactly like an extension would.
      if (gap <= editGapMs && withinEditDistance(mark.query, query, maxEditDistance)) {
        mark = { query: query, meta: meta };
        startIdleTimer();
        return;
      }

      // Genuine divergence. Correction 1: the mark is a completed, real
      // search the customer abandoned for this new one -- commit it now,
      // by itself, before the new candidate starts. This is the same
      // action whether or not the mark was currently held below a
      // contraction: either way, the mark is the query that actually
      // finished, and it is never silently dropped.
      var old = mark;
      mark = { query: query, meta: meta };
      onCommit(old.query, old.meta);
      startIdleTimer();
    }

    // Discards the mark without logging it. For the case where the
    // customer backs all the way out of a search (clears the box below
    // the surface's own minimum-query floor, or the search panel resets
    // for an unrelated reason) rather than finishing one -- there is no
    // search to report.
    function reset() {
      clearIdleTimer();
      mark = null;
      lastSettleAt = null;
    }

    // The query that would be committed if flush() ran right now, or null.
    // This is the high-water mark, not necessarily the literal string the
    // customer's field currently shows (see correction 1): if the burst is
    // currently contracted below the mark, this still reports the mark.
    function pendingQuery() {
      return mark ? mark.query : null;
    }

    return {
      settle: settle,
      flush: flush,
      reset: reset,
      pendingQuery: pendingQuery,
    };
  }

  return {
    COMMIT_IDLE_MS: COMMIT_IDLE_MS,
    MAX_EDIT_DISTANCE: MAX_EDIT_DISTANCE,
    EDIT_CONTINUATION_GAP_MS: EDIT_CONTINUATION_GAP_MS,
    CONTINUATION_STEM_CHARS: CONTINUATION_STEM_CHARS,
    isPrefixRelated: isPrefixRelated,
    isTypingContinuation: isTypingContinuation,
    levenshtein: levenshtein,
    withinEditDistance: withinEditDistance,
    createSettleTracker: createSettleTracker,
  };
});
